import type { FC } from "react";
import { useCallback, useState } from "react";

import { resendSignUpCode, signIn, signUp } from "@aws-amplify/auth";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { getPaths } from "core/getPaths";
import { OnboardingForm, PasswordInput } from "shared/Components";
import { useAuth, useInvitation, useUser } from "shared/hooks";
import { Button, Input, Text } from "shared/uikit";
import { ButtonSizesEnum, ButtonVariantsEnum, TextVariantsEnum } from "shared/uikit/types";
import { validateEmail } from "utils/serviceUtils/validators";

import SocialLogin from "./SocialLogin";
import type { AuthenticationMode } from "./types";

interface CredentialsStepProps {
	mode: Exclude<AuthenticationMode, "signIn">;
	redirectUrl?: string;
}

interface FormInputs {
	email: string;
	password: string;
}

const { auth } = getPaths();

const CredentialsStep: FC<CredentialsStepProps> = ({ mode, redirectUrl }) => {
	const { t } = useTranslation();
	const isRegister = mode === "register";
	const { control, handleSubmit, formState, setError, watch } = useForm<FormInputs>({
		mode: "onChange"
	});
	const { getData: getUserData } = useUser();
	const { tempCredentials } = getUserData();
	const { getData: getInvitationData } = useInvitation();
	const { tempInvitation } = getInvitationData();
	const [invitationInfo] = useState(tempInvitation);
	const email = watch("email") || tempCredentials?.email;
	const [submitting, setSubmitting] = useState(false);
	const [showPasswordRules, setShowPasswordRules] = useState(false);
	const [isValidPassword, setIsValidPassword] = useState(!isRegister);
	const { loginWithGoogle, loginWithApple, loginWithFacebook, logoutPrevUser, generateLink } = useAuth({
		invitationInfo,
		redirectUrl
	});
	const { errors } = formState;
	const navigate = useNavigate();

	const redirectToVerifyEmail = useCallback(
		async (username?: string) => {
			try {
				if (username) {
					await resendSignUpCode({ username });
				}
				navigate(generateLink(auth.otp.getPath()));
			} catch (error) {
				console.log(error);
			}
		},
		[navigate, generateLink]
	);

	const registerNewUser = async (password: string) => {
		try {
			const result = await signUp({
				username: tempCredentials!.username,
				password,
				options: {
					userAttributes: { email: tempCredentials!.email },
					autoSignIn: true
				}
			});

			if (result.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
				await redirectToVerifyEmail();
			}
		} catch (error: any) {
			const errorCode = error?.code || error?.name;
			if (errorCode === "UsernameExistsException") {
				return loginUser(password);
			}
			if (errorCode === "UserAlreadyAuthenticatedException") {
				logoutPrevUser();
				await loginUser(password);
			}
			console.log({ signUpError: error });
		}
	};

	const loginUser = async (password: string) => {
		try {
			const result = await signIn({
				username: tempCredentials!.username,
				password
			});

			if (result.isSignedIn) {
				return navigate(generateLink(auth.complete.getPath()));
			}
			if (result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
				navigate(generateLink(auth.otp.getPath()));
			}
			if (result.nextStep.signInStep === "CONFIRM_SIGN_UP") {
				await redirectToVerifyEmail(tempCredentials!.username);
			}
		} catch (error: any) {
			const errorCode = error?.code || error?.name;
			if (errorCode === "UserNotConfirmedException") {
				await redirectToVerifyEmail(tempCredentials!.username);
				return;
			}
			if (errorCode === "UserNotFoundException") return registerNewUser(password);
			if (errorCode === "NotAuthorizedException") {
				setError("password", { message: t("error:incorrect", { field: t("password") }) });
			}
			if (errorCode === "UserAlreadyAuthenticatedException") {
				logoutPrevUser();
				await loginUser(password);
			}
		}
	};

	const onSubmit: SubmitHandler<FormInputs> = async data => {
		if (!tempCredentials) return;
		setSubmitting(true);
		try {
			isRegister ? await registerNewUser(data.password) : await loginUser(data.password);
		} catch (error) {
			console.error(error);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<OnboardingForm
			title={isRegister ? t("sign_up") : t("log_in")}
			subtitle={isRegister ? t("please_enter_your_details_to_create_an_account") : t("log_in_with_any_method")}
			onSubmit={handleSubmit(onSubmit)}
		>
			{!isRegister && (
				<SocialLogin
					continueWith={t("continue_with")}
					loginWithApple={loginWithApple}
					loginWithFacebook={loginWithFacebook}
					loginWithGoogle={loginWithGoogle}
					withDivider
				/>
			)}
			<Controller
				name="email"
				control={control}
				rules={{
					required: t("error:is_required", { field: t("email") }),
					validate: value => validateEmail(value) || t("error:not_valid", { field: t("email") })
				}}
				defaultValue={tempCredentials?.email}
				render={({ field: { value } }) => (
					<Input
						size={ButtonSizesEnum.UNDER_MD}
						placeholder={t("email")}
						value={value ? value.toLowerCase() : value}
						onChange={() => {}}
						error={!!errors.email?.message}
						errorText={errors.email?.message}
						disabled
					/>
				)}
			/>
			<Controller
				name="password"
				control={control}
				rules={{ required: t("error:is_required", { field: t("password") }) }}
				render={({ field: { onChange, value } }) => (
					<div className="relative mt-4">
						<PasswordInput
							onChange={onChange}
							value={value}
							error={errors.password?.message}
							setIsValidPassword={setIsValidPassword}
							placeholder={t("password")}
							size={ButtonSizesEnum.UNDER_MD}
							includeRules={isRegister && showPasswordRules}
							onFocus={() => setShowPasswordRules(true)}
							onBlur={() => setShowPasswordRules(false)}
						/>
					</div>
				)}
			/>
			{!isRegister && (
				<div className="flex justify-end mt-2">
					<Text variants={TextVariantsEnum.BodyMedium} className="text-primary-600">
						<Link to={`${auth.forgotPassword.getPath()}?email=${encodeURIComponent(email || "")}`}>
							{t("forgot_password")}
						</Link>
					</Text>
				</div>
			)}
			<Button
				className="mt-6"
				size={ButtonSizesEnum.LG}
				variant={ButtonVariantsEnum.FILLED}
				id="continue-with-email-btn"
				disabled={!!Object.keys(errors).length || submitting || !isValidPassword}
				type="submit"
				loading={submitting}
			>
				{t("contin")}
			</Button>
		</OnboardingForm>
	);
};

export default CredentialsStep;
